import React, { useEffect, useCallback, useMemo, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getCurrentOrganization,
  getLoadingOrganizations,
} from 'reduxModules/organizations/selectors';
import { updateLoading } from 'reduxModules/devices';
import { LoadingWithText, useBreadcrumb, useConfirm } from 'components';
import socketClient from 'socketClient';
import classnames from 'classnames/bind';
import styles from './Organization.scss';
import { leaveOrganization } from 'reduxModules/organizations';
import OrganizationHeader from './OrganizationHeader';
import InstrumentsSection from './InstrumentsSection';
import TeamsSection from './TeamsSection';
import SettingsSection from './SettingsSection';

const cx = classnames.bind(styles);

export function Organization() {
  const dispatch = useDispatch();
  const { confirm } = useConfirm();
  const { orgSlug } = useParams();

  const currentOrganization = useSelector(getCurrentOrganization);
  const loading = useSelector(getLoadingOrganizations);

  const {
    devices: instruments = [],
    teams = [],
    memberId,
    $permissions = {},
  } = currentOrganization;
  const { canEditOrganization } = $permissions;

  const subscribeToDevices = useCallback(async (slug) => {
    try {
      await dispatch(updateLoading(true));
      await socketClient.subscribeToDevices(slug);
    } finally {
      await dispatch(updateLoading(false));
    }
  }, [dispatch]);

  const unsubscribeFromDevices = useCallback(async (slug) => {
    await socketClient.unsubscribeFromDevices(slug);
  }, []);

  const handleLeaveOrganization = useCallback(() => {
    confirm(
      `Are you sure you want to leave the organization "${currentOrganization.name}"?`,
      () => dispatch(leaveOrganization(memberId))
    );
  }, [confirm, currentOrganization.name, memberId, dispatch]);

  useEffect(() => {
    subscribeToDevices(orgSlug);
    return () => {
      unsubscribeFromDevices(orgSlug);
    };
  }, [orgSlug, subscribeToDevices, unsubscribeFromDevices]);

  useBreadcrumb(
    () => [
      {
        text: currentOrganization?.name,
      },
    ],
    [currentOrganization]
  );

  const protocolCount = useMemo(() =>
    teams.reduce((sum, team) => sum + team.$protocolCount, 0),
    [teams]
  );

  return (
    <div className={cx('container')}>
      {loading && <LoadingWithText message="Loading organization..." />}
      {!loading && (
        <>
          <OrganizationHeader
            name={currentOrganization.name}
            orgSlug={orgSlug}
            memberCount={currentOrganization.$memberCount}
            instrumentCount={instruments.length}
            protocolCount={protocolCount}
            canEditOrganization={canEditOrganization}
          />
          <InstrumentsSection
            instruments={instruments}
            orgSlug={orgSlug}
            canEditOrganization={canEditOrganization}
          />
          <TeamsSection
            teams={teams}
            canEditOrganization={canEditOrganization}
          />
          <SettingsSection onLeaveOrganization={handleLeaveOrganization} />
        </>
      )}
    </div>
  );
};

export default memo(Organization);
